import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Modal, ModalFooter, useConfirm } from '@tkpod/ui';
import {
  DATE_DISPLAY_FORMATS,
  TEXT_TRANSFORMS,
  SWATCH_SIZES,
  customSizeConfigSchema,
  displayRulesSchema,
  getOptionType,
  isLegacyOptionType,
  isSingleSelectOptionType,
  isSizedUploadOptionType,
  normalizeChoices,
  pickerOptionTypesFor,
  productOptionChoicesSchema,
  type DisplayRule,
  type ProductOptionChoice,
  type ProductOptionType,
} from '@tkpod/shared';
import { ImageUploadInput } from '@/components/ImageUploadInput';
import { DisplayRulesEditor, type RuleAxisRef, type RuleOptionRef } from './DisplayRulesEditor';
import {
  slugOptionKey,
  type OptionWritePayload,
  type ProductOptionRow,
} from './product-option-model';

const SECTIONS = ['General', 'Configuration', 'Choices', 'Conditions', 'Pricing', 'Visibility'] as const;

/** One line of help per charge type, shown under the Charge type select. */
export const OPTION_CHARGE_HELP = {
  NONE: 'No extra charge. The option is recorded with the order only.',
  PER_UNIT: 'A fixed amount added to every unit. 10 pieces × $0.50 = $5.00.',
  PER_LINE:
    'A fixed amount added once per line. At Shopify checkout the fee is spread evenly over the units.',
  PERCENT_OF_VARIANT: 'A percentage of the unit price. 10% on a $20.00 unit adds $2.00.',
} as const;

const UPLOAD_TYPES = new Set(['FILE_UPLOAD', 'IMAGE_UPLOAD_SIZED', 'GANGSHEET_UPLOAD_SIZED']);

interface AxisOption extends RuleAxisRef {
  valueCount: number;
}

const OPTION_TYPE_INFO: Record<string, { label: string; description: string; group: string }> = {
  TEXT: { label: 'Text', description: 'Single line of text', group: 'Text' },
  TEXTAREA: { label: 'Text area', description: 'Several lines of text', group: 'Text' },
  STRING: { label: 'String', description: 'Legacy, switch to Text', group: 'Legacy' },
  NUMBER: { label: 'Number', description: 'A number with min, max, and step', group: 'Other' },
  CHECKBOX: { label: 'Checkbox', description: 'Single yes/no', group: 'Choice' },
  CHECKBOX_GROUP: { label: 'Checkbox group', description: 'Pick several', group: 'Choice' },
  BOOLEAN: { label: 'Boolean', description: 'Legacy, switch to Checkbox', group: 'Legacy' },
  RADIO: { label: 'Radio', description: 'Pick one', group: 'Choice' },
  SELECT: { label: 'Dropdown', description: 'Pick one from a list', group: 'Choice' },
  SWATCH: { label: 'Swatch', description: 'Color or image, pick one', group: 'Choice' },
  BUTTON_GROUP: { label: 'Button group', description: 'Pick one as a button', group: 'Choice' },
  DATE: { label: 'Date', description: 'A calendar date', group: 'Other' },
  CUSTOM_SIZE: { label: 'Dimension', description: 'Width and height with a unit', group: 'Other' },
  INFO: { label: 'Info', description: 'Text the customer reads, not a choice', group: 'Other' },
  FILE_UPLOAD: { label: 'File upload', description: 'The customer uploads a file', group: 'Upload' },
  IMAGE_UPLOAD_SIZED: {
    label: 'DTF by Size upload',
    description: 'Artwork priced from its trimmed size',
    group: 'Upload',
  },
  GANGSHEET_UPLOAD_SIZED: {
    label: 'Gang sheet upload',
    description: 'A fixed-width sheet priced from its height',
    group: 'Upload',
  },
};

const PICKER_GROUPS = ['Text', 'Choice', 'Upload', 'Other', 'Legacy'] as const;

function prepareOptionConfig(
  optionType: string,
  config: unknown,
  blanks: readonly PricingBlank[],
): { config: unknown; error?: undefined } | { config?: undefined; error: string } {
  const base = configWithoutGallery(config);
  if (optionType !== 'GANGSHEET_UPLOAD_SIZED' || !base || typeof base !== 'object' || Array.isArray(base)) {
    return { config: base };
  }
  const record = base as Record<string, unknown>;
  const pricing =
    record.sizePricing && typeof record.sizePricing === 'object' && !Array.isArray(record.sizePricing)
      ? (record.sizePricing as Record<string, unknown>)
      : {};
  const binding =
    record.variantBinding && typeof record.variantBinding === 'object' && !Array.isArray(record.variantBinding)
      ? (record.variantBinding as Record<string, unknown>)
      : {};
  const sizing =
    record.sizing && typeof record.sizing === 'object' && !Array.isArray(record.sizing)
      ? (record.sizing as Record<string, unknown>)
      : {};
  const active = blanks.filter((item) => item.isActive).length;
  if (pricing.mode === 'VARIANT_BAND' && active < 2) {
    return {
      error:
        'A single variant uses area, longest side, or height. Nearest taller variant needs at least two variants with a height.',
    };
  }
  return {
    config: {
      ...record,
      sizing: { ...sizing, allowResize: false, allowStretch: false, allowDetectedSize: true },
      variantBinding: {
        ...binding,
        customerCanChangeVariant: false,
        ...(pricing.mode === 'VARIANT_BAND' ? { defaultVariantRule: 'HEIGHT_CEIL' } : {}),
      },
    },
  };
}

function configWithoutGallery(config: unknown): unknown {
  if (!config || typeof config !== 'object' || Array.isArray(config)) return config;
  const sources = (config as { sources?: unknown }).sources;
  if (!Array.isArray(sources) || !sources.includes('GALLERY')) return config;
  const next = sources.filter((item) => item !== 'GALLERY');
  return { ...(config as object), sources: next.length > 0 ? next : ['DEVICE'] };
}

export function OptionEditorModal({
  open,
  initial,
  axes,
  siblings,
  saving,
  surchargeEnabled,
  pricingBlanks = [],
  productType,
  designerMode,
  serverFailure,
  onClose,
  onSave,
  onDirtyChange,
  onDraft,
}: {
  open: boolean;
  initial: ProductOptionRow | null;
  axes: AxisOption[];
  siblings: RuleOptionRef[];
  saving: boolean;
  surchargeEnabled: boolean;
  pricingBlanks?: PricingBlank[];
  productType: string;
  designerMode: string;
  serverFailure: { message: string; fieldErrors: { field: string; message: string }[] } | null;
  onClose: () => void;
  onSave: (payload: OptionWritePayload) => void;
  onDirtyChange: (dirty: boolean) => void;
  onDraft: (payload: OptionWritePayload) => void;
}) {
  const { confirm, dialog } = useConfirm();
  const [section, setSection] = useState<(typeof SECTIONS)[number]>('General');
  const [baseline] = useState(() => initialPayload(initial));
  const [draft, setDraft] = useState<OptionWritePayload>(baseline);
  const [keyTouched, setKeyTouched] = useState(Boolean(initial?.key));
  const [error, setError] = useState<string | null>(null);
  const [centsError, setCentsError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Map<string, string>>(new Map());
  const dirty = JSON.stringify(draft) !== JSON.stringify(baseline);

  const definition = getOptionType(draft.optionType as ProductOptionType);
  const usesChoices = definition?.capabilities.usesChoices === true;

  const grouped = useMemo(
    () => groupTypes(draft.optionType, { type: productType, designerMode }),
    [draft.optionType, productType, designerMode],
  );

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (!serverFailure) return;
    const map = new Map(serverFailure.fieldErrors.map((item) => [item.field, item.message]));
    setFieldErrors(map);
    setError(serverFailure.message);
    const field = serverFailure.fieldErrors[0]?.field ?? '';
    if (field.startsWith('config.pricing')) setSection('Pricing');
    else if (field.startsWith('config')) setSection('Configuration');
    else if (field.startsWith('values')) setSection('Choices');
    else if (field.startsWith('displayRules')) setSection('Conditions');
    else setSection('General');
  }, [serverFailure]);

  const onDraftRef = useRef(onDraft);
  onDraftRef.current = onDraft;
  useEffect(() => {
    onDraftRef.current(draft);
  }, [draft]);

  const set = (patch: Partial<OptionWritePayload>) => {
    setDraft((current) => ({ ...current, ...patch }));
  };

  const close = async () => {
    if (!dirty) {
      onClose();
      return;
    }
    const ok = await confirm({
      title: 'Discard changes',
      description: 'This option has unsaved changes.',
      confirmLabel: 'Discard',
      tone: 'danger',
    });
    if (ok) onClose();
  };

  const changeType = (optionType: string) => {
    const next = getOptionType(optionType as ProductOptionType);
    const role = 'ADDON' as const;
    set({
      optionType,
      config: next?.defaultConfig ?? {},
      values: next?.capabilities.usesChoices ? draft.values : [],
      role,
      boundOptionTypeId: undefined,
    });
  };

  const submit = () => {
    const prepared = prepareOptionConfig(draft.optionType, configWithoutGallery(draft.config), pricingBlanks);
    if (prepared.error) {
      setError(prepared.error);
      setSection('Pricing');
      return;
    }
    const configParsed = definition?.configSchema.safeParse(prepared.config ?? {});
    if (!definition || !configParsed?.success) {
      const issues = configParsed && !configParsed.success ? issueMap(configParsed.error.issues) : new Map<string, string>();
      setFieldErrors(issues);
      setError(configParsed && !configParsed.success
        ? summarize(configParsed.error.issues)
        : 'This option type is not available.');
      setSection('Configuration');
      return;
    }
    if (centsError) {
      setError(centsError);
      setSection('Pricing');
      return;
    }
    if (draft.priceDeltaCents != null && !Number.isInteger(draft.priceDeltaCents)) {
      setError('Price must be a whole number of cents.');
      setSection('Pricing');
      return;
    }
    if (draft.priceDeltaPercent != null && !Number.isFinite(draft.priceDeltaPercent)) {
      setError('Percent must be a number.');
      setSection('Pricing');
      return;
    }
    const normalized = normalizeChoices(draft.optionType, usesChoices ? draft.values : []);
    const choices = productOptionChoicesSchema.safeParse(normalized);
    if (!choices.success) {
      setFieldErrors(issueMap(choices.error.issues));
      setError(summarize(choices.error.issues));
      setSection('Choices');
      return;
    }
    const rules = draft.displayRules == null
      ? { success: true as const, data: null }
      : displayRulesSchema.safeParse(draft.displayRules);
    if (!rules.success) {
      setFieldErrors(issueMap(rules.error.issues));
      setError(summarize(rules.error.issues));
      setSection('Conditions');
      return;
    }
    if (draft.role === 'VARIANT_SELECTOR' && !draft.boundOptionTypeId) {
      setError('Bind the variant selector to an axis.');
      setSection('General');
      return;
    }
    setError(null);
    setFieldErrors(new Map());
    onSave({
      ...draft,
      config: configParsed.data,
      values: choices.data,
      displayRules: rules.data,
    });
  };

  return (
    <Modal open={open} onClose={() => void close()} title={initial ? 'Edit option' : 'Add option'} size="lg">
      {dialog}
      <div className="mb-4 flex flex-wrap gap-2">
        {SECTIONS.map((item) => (
          <button
            key={item}
            type="button"
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              section === item ? 'bg-brand-500 text-white' : 'bg-surface-secondary text-text-secondary'
            }`}
            onClick={() => setSection(item)}
          >
            {item}
          </button>
        ))}
      </div>
      {error ? <p className="mb-3 text-sm text-danger-700">{error}</p> : null}
      {section === 'General' && (
        <div className="space-y-3">
          <label className="block text-sm">
            <span className="text-text-secondary">Label</span>
            <input
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
              value={draft.displayName}
              onChange={(event) => {
                const displayName = event.target.value;
                set({
                  displayName,
                  key: keyTouched ? draft.key : slugOptionKey(displayName),
                });
              }}
            />
          </label>
          <label className="block text-sm">
            <span className="text-text-secondary">Key</span>
            <input
              className="mt-1 w-full rounded-md border border-border px-3 py-2 font-mono text-sm"
              value={draft.key}
              onChange={(event) => {
                setKeyTouched(true);
                set({ key: event.target.value });
              }}
            />
          </label>
          <label className="block text-sm">
            <span className="text-text-secondary">Type</span>
            <select
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
              value={draft.optionType}
              onChange={(event) => changeType(event.target.value)}
            >
              {grouped.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.types.map((type) => {
                    const info = OPTION_TYPE_INFO[type];
                    return (
                      <option key={type} value={type}>
                        {info ? `${info.label} — ${info.description}` : type.replace(/_/g, ' ')}
                      </option>
                    );
                  })}
                </optgroup>
              ))}
            </select>
          </label>
          {isLegacyOptionType(draft.optionType) ? (
            <p className="text-sm text-warning">
              Legacy type. Switch to {draft.optionType === 'BOOLEAN' ? 'Checkbox' : 'Text'} so new templates stay consistent.
            </p>
          ) : null}
          <DefaultValueField
            optionType={draft.optionType}
            value={draft.defaultValue}
            onChange={(defaultValue) => set({ defaultValue })}
          />
          <label className="block text-sm">
            <span className="text-text-secondary">Role</span>
            <select
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
              value={draft.role}
              onChange={(event) => {
                const role = event.target.value as OptionWritePayload['role'];
                set({
                  role,
                  boundOptionTypeId:
                    role === 'VARIANT_SELECTOR'
                      ? draft.boundOptionTypeId ?? (axes.length === 1 ? axes[0]!.optionTypeId : undefined)
                      : undefined,
                });
              }}
            >
              <option value="ADDON">Add-on</option>
              {draft.role === 'VARIANT_SELECTOR' ? (
                <option value="VARIANT_SELECTOR">Variant selector (no longer offered)</option>
              ) : null}
            </select>
          </label>
          {draft.role === 'VARIANT_SELECTOR' && (
            <label className="block text-sm">
              <span className="text-text-secondary">Bound axis</span>
              <select
                className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
                value={draft.boundOptionTypeId ?? ''}
                onChange={(event) => set({ boundOptionTypeId: event.target.value || undefined })}
              >
                <option value="">Select an axis</option>
                {axes.map((axis) => (
                  <option key={axis.optionTypeId} value={axis.optionTypeId}>
                    {axis.label} ({axis.valueCount} values)
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.isRequired}
              onChange={(event) => set({ isRequired: event.target.checked })}
            />
            Required
          </label>
          <label className="block text-sm">
            <span className="text-text-secondary">Help text</span>
            <input
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
              value={draft.helpText ?? ''}
              onChange={(event) => set({ helpText: event.target.value })}
            />
          </label>
          <label className="block text-sm">
            <span className="text-text-secondary">Placeholder</span>
            <input
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
              value={draft.placeholder ?? ''}
              onChange={(event) => set({ placeholder: event.target.value })}
            />
          </label>
        </div>
      )}
      {section === 'Configuration' && (
        <ConfigFields
          optionType={draft.optionType}
          config={asRecord(draft.config)}
          fieldErrors={fieldErrors}
          onChange={(config) => set({ config })}
        />
      )}
      {section === 'Choices' && (
        usesChoices ? (
          <ChoicesEditor
            optionType={draft.optionType}
            values={draft.values}
            fieldErrors={fieldErrors}
            surchargeEnabled={surchargeEnabled}
            onChange={(values) => set({ values })}
          />
        ) : (
          <p className="text-sm text-text-secondary">This type does not use choices.</p>
        )
      )}
      {section === 'Conditions' && (
        <DisplayRulesEditor
          rules={draft.displayRules ?? []}
          options={siblings}
          axes={axes}
          fieldErrors={fieldErrors}
          onChange={(displayRules) => set({ displayRules })}
        />
      )}
      {section === 'Pricing' && (
        <div className="space-y-3">
          <p className="text-sm text-text-secondary">
            Adds a charge when the customer selects this option. Portal or Shopify charging follows the platform surcharge mode.
          </p>
          <label className="block text-sm">
            <span className="text-text-secondary">Charge type</span>
            <select
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
              value={draft.priceDeltaMode}
              onChange={(event) =>
                set({ priceDeltaMode: event.target.value as OptionWritePayload['priceDeltaMode'] })
              }
            >
              <option value="NONE">None</option>
              <option value="PER_UNIT">Per unit</option>
              <option value="PER_LINE">Per line</option>
              <option value="PERCENT_OF_VARIANT">Percent of variant</option>
            </select>
            <span className="mt-1 block text-xs text-text-tertiary">
              {OPTION_CHARGE_HELP[draft.priceDeltaMode]}
            </span>
          </label>
          {draft.priceDeltaMode === 'PERCENT_OF_VARIANT' ? (
            <label className="block text-sm">
              <span className="text-text-secondary">Percent</span>
              <BufferedInput
                type="number"
                className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
                min={0}
                step={1}
                value={draft.priceDeltaPercent == null ? '' : String(draft.priceDeltaPercent)}
                onCommit={(raw) => set({ priceDeltaPercent: optionalNumber(raw) })}
              />
            </label>
          ) : draft.priceDeltaMode !== 'NONE' ? (
            <label className="block text-sm">
              <span className="text-text-secondary">Charge (cents)</span>
              <BufferedInput
                type="number"
                className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
                min={0}
                step={1}
                value={draft.priceDeltaCents == null ? '' : String(draft.priceDeltaCents)}
                onCommit={(raw) => {
                  const parsed = wholeCents(raw);
                  if (!parsed.ok) {
                    setCentsError('Enter a whole number of cents. 50 is $0.50.');
                    return;
                  }
                  setCentsError(null);
                  set({ priceDeltaCents: parsed.cents });
                }}
              />
              <span className="mt-1 block text-xs text-text-tertiary">
                Whole cents. 50 is $0.50. This is the default for every choice. Give a choice its own price under Choices to override it.
              </span>
              {centsError ? <span className="mt-1 block text-xs text-danger-700">{centsError}</span> : null}
            </label>
          ) : null}
          {isSizedUploadOptionType(draft.optionType) && (
            <SizePricingFields
              optionType={draft.optionType}
              config={asRecord(draft.config)}
              blanks={pricingBlanks}
              onChange={(config) => set({ config })}
            />
          )}
          {draft.optionType === 'CUSTOM_SIZE' && (
            <DimensionPricePicker
              config={draft.config}
              blanks={pricingBlanks}
              error={fieldErrors.get('config.pricingVariantId')}
              onChange={(pricingVariantId) =>
                set({ config: { ...asRecord(draft.config), pricingVariantId } })
              }
            />
          )}
        </div>
      )}
      {section === 'Visibility' && (
        <div className="space-y-3 text-sm">
          <Check label="Show in storefront" checked={draft.showInStorefront} onChange={(showInStorefront) => set({ showInStorefront })} />
          <Check label="Show in portal" checked={draft.showInPortal} onChange={(showInPortal) => set({ showInPortal })} />
          <Check label="Show to provider" checked={draft.showToProvider} onChange={(showToProvider) => set({ showToProvider })} />
          <Check label="Hide in cart properties" checked={draft.hiddenInCart} onChange={(hiddenInCart) => set({ hiddenInCart })} />
          <label className="block">
            <span className="text-text-secondary">Line item label</span>
            <input
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
              value={draft.lineItemLabel ?? ''}
              onChange={(event) => set({ lineItemLabel: event.target.value })}
            />
          </label>
        </div>
      )}
      <ModalFooter>
        <Button variant="outline" onClick={() => void close()} disabled={saving}>Cancel</Button>
        <Button loading={saving} onClick={submit}>Save option</Button>
      </ModalFooter>
    </Modal>
  );
}

function initialPayload(row: ProductOptionRow | null): OptionWritePayload {
  if (row) {
    return {
      key: row.key,
      displayName: row.displayName,
      optionType: row.optionType,
      role: row.role,
      description: row.description ?? undefined,
      helpText: row.helpText ?? undefined,
      placeholder: row.placeholder ?? undefined,
      defaultValue: row.defaultValue ?? undefined,
      isRequired: row.isRequired,
      isActive: row.isActive,
      config: row.config ?? {},
      values: Array.isArray(row.values) ? (row.values as ProductOptionChoice[]) : [],
      displayRules: Array.isArray(row.displayRules) ? (row.displayRules as DisplayRule[]) : [],
      boundOptionTypeId: row.boundOptionTypeId ?? undefined,
      lineItemLabel: row.lineItemLabel ?? undefined,
      showInStorefront: row.showInStorefront,
      showInPortal: row.showInPortal,
      showToProvider: row.showToProvider,
      hiddenInCart: row.hiddenInCart,
      priceDeltaMode: row.priceDeltaMode,
      priceDeltaCents: row.priceDeltaCents ?? undefined,
      priceDeltaPercent:
        row.priceDeltaPercent == null ? undefined : Number(row.priceDeltaPercent),
    };
  }
  const type = 'TEXT' as ProductOptionType;
  return {
    key: 'option',
    displayName: '',
    optionType: type,
    role: 'ADDON',
    isRequired: false,
    isActive: true,
    config: getOptionType(type)?.defaultConfig ?? {},
    values: [],
    displayRules: [],
    showInStorefront: true,
    showInPortal: true,
    showToProvider: true,
    hiddenInCart: false,
    priceDeltaMode: 'NONE',
  };
}

function groupTypes(
  current: string,
  template: { type: string; designerMode: string },
): Array<{ label: string; types: string[] }> {
  const allowed = new Set(pickerOptionTypesFor(template));
  const types = Object.keys(OPTION_TYPE_INFO).filter((type) => {
    const group = OPTION_TYPE_INFO[type]?.group;
    if (!PICKER_GROUPS.includes(group as (typeof PICKER_GROUPS)[number])) return false;
    if (group === 'Legacy') return type === current;
    if (isSizedUploadOptionType(type)) return allowed.has(type) || type === current;
    return true;
  });
  if (!types.includes(current)) types.push(current);
  const grouped = PICKER_GROUPS.map((label) => ({
    label,
    types: types.filter((type) => OPTION_TYPE_INFO[type]?.group === label),
  })).filter((group) => group.types.length > 0);
  const unknown = types.filter((type) => !OPTION_TYPE_INFO[type]);
  return unknown.length > 0 ? [...grouped, { label: 'Current', types: unknown }] : grouped;
}

function ConfigFields({
  optionType,
  config,
  fieldErrors,
  onChange,
}: {
  optionType: string;
  config: Record<string, unknown>;
  fieldErrors: Map<string, string>;
  onChange: (config: Record<string, unknown>) => void;
}) {
  if (optionType === 'TEXT' || optionType === 'TEXTAREA' || optionType === 'STRING') {
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        <Num label="Min length" value={config.minLength} error={fieldErrors.get('config.minLength')} onChange={(minLength) => onChange({ ...config, minLength })} />
        <Num label="Max length" value={config.maxLength} error={fieldErrors.get('config.maxLength')} onChange={(maxLength) => onChange({ ...config, maxLength })} />
        <label className="block text-sm sm:col-span-2">
          <span className="text-text-secondary">Pattern</span>
          <input
            className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
            value={typeof config.pattern === 'string' ? config.pattern : ''}
            onChange={(event) => onChange({ ...config, pattern: event.target.value || null })}
          />
        </label>
        <label className="block text-sm">
          <span className="text-text-secondary">Text transform</span>
          <select
            className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
            value={typeof config.textTransform === 'string' ? config.textTransform : 'NONE'}
            onChange={(event) => onChange({ ...config, textTransform: event.target.value })}
          >
            {TEXT_TRANSFORMS.map((transform) => (
              <option key={transform} value={transform}>{transform}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={config.showCounter === true}
            onChange={(event) => onChange({ ...config, showCounter: event.target.checked })}
          />
          Show character counter
        </label>
      </div>
    );
  }
  if (optionType === 'NUMBER') {
    return (
      <div className="grid gap-3 sm:grid-cols-3">
        <Num label="Min" value={config.min} error={fieldErrors.get('config.min')} onChange={(min) => onChange({ ...config, min })} />
        <Num label="Max" value={config.max} error={fieldErrors.get('config.max')} onChange={(max) => onChange({ ...config, max })} />
        <Num label="Step" value={config.step} step="any" error={fieldErrors.get('config.step')} onChange={(step) => onChange({ ...config, step })} />
      </div>
    );
  }
  if (optionType === 'INFO') {
    return (
      <label className="block text-sm">
        <span className="text-text-secondary">Body</span>
        <textarea
          className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
          rows={4}
          value={typeof config.body === 'string' ? config.body : ''}
          onChange={(event) => onChange({ ...config, body: event.target.value })}
        />
      </label>
    );
  }
  if (optionType === 'CUSTOM_SIZE') {
    return <DimensionFields config={config} fieldErrors={fieldErrors} onChange={onChange} />;
  }
  if (optionType === 'DATE') {
    const blackout = Array.isArray(config.blackoutDates) ? config.blackoutDates.filter((item) => typeof item === 'string').join('\n') : '';
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="text-text-secondary">Earliest date</span>
          <input type="date" className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm" value={typeof config.minDate === 'string' ? config.minDate : ''} onChange={(event) => onChange({ ...config, minDate: event.target.value || null })} />
        </label>
        <label className="block text-sm">
          <span className="text-text-secondary">Latest date</span>
          <input type="date" className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm" value={typeof config.maxDate === 'string' ? config.maxDate : ''} onChange={(event) => onChange({ ...config, maxDate: event.target.value || null })} />
        </label>
        <label className="block text-sm">
          <span className="text-text-secondary">Display format</span>
          <select className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm" value={typeof config.displayFormat === 'string' ? config.displayFormat : 'LOCALE'} onChange={(event) => onChange({ ...config, displayFormat: event.target.value })}>
            {DATE_DISPLAY_FORMATS.map((format) => <option key={format} value={format}>{format}</option>)}
          </select>
        </label>
        <label className="block text-sm sm:col-span-2">
          <span className="text-text-secondary">Unavailable dates (one YYYY-MM-DD per line)</span>
          <textarea className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm" rows={3} value={blackout} onChange={(event) => onChange({ ...config, blackoutDates: event.target.value.split(/\s+/).map((item) => item.trim()).filter(Boolean) })} />
        </label>
      </div>
    );
  }
  if (optionType === 'SWATCH') {
    const size = SWATCH_SIZES.includes(config.swatchSize as (typeof SWATCH_SIZES)[number])
      ? String(config.swatchSize)
      : 'SMALL';
    return (
      <label className="block text-sm">
        <span className="text-text-secondary">Swatch size</span>
        <select
          className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
          value={size}
          onChange={(event) => onChange({ ...config, swatchSize: event.target.value })}
        >
          <option value="SMALL">Small (40 px)</option>
          <option value="MEDIUM">Medium (64 px)</option>
          <option value="LARGE">Large (96 px)</option>
        </select>
        <span className="mt-1 block text-xs text-text-tertiary">
          This option on the portal and the storefront. Variant color swatches stay 40 px.
        </span>
      </label>
    );
  }
  if (optionType === 'CHECKBOX') {
    return (
      <label className="block text-sm">
        <span className="text-text-secondary">Text next to the box</span>
        <input className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm" value={typeof config.label === 'string' ? config.label : ''} onChange={(event) => onChange({ ...config, label: event.target.value })} />
      </label>
    );
  }
  if (optionType === 'CHECKBOX_GROUP') {
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        <Num label="Minimum selections" value={config.minSelections} error={fieldErrors.get('config.minSelections')} onChange={(minSelections) => onChange({ ...config, minSelections })} />
        <Num label="Maximum selections" value={config.maxSelections} error={fieldErrors.get('config.maxSelections')} onChange={(maxSelections) => onChange({ ...config, maxSelections })} />
      </div>
    );
  }
  if (UPLOAD_TYPES.has(optionType)) {
    const accept = asRecord(config.accept);
    const mimes = Array.isArray(accept.mimeTypes) ? accept.mimeTypes.join(', ') : '';
    return (
      <div className="space-y-3">
        <label className="block text-sm">
          <span className="text-text-secondary">Accepted MIME types</span>
          <input
            className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
            value={mimes}
            onChange={(event) =>
              onChange({
                ...config,
                accept: {
                  ...accept,
                  mimeTypes: event.target.value.split(',').map((item) => item.trim()).filter(Boolean),
                },
              })
            }
          />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <Num
            label="Max file MB"
            value={accept.maxFileMb}
            onChange={(maxFileMb) => onChange({ ...config, accept: { ...accept, maxFileMb } })}
          />
          <Num
            label="Max files"
            value={accept.maxFiles}
            onChange={(maxFiles) => onChange({ ...config, accept: { ...accept, maxFiles } })}
          />
        </div>
        {isSizedUploadOptionType(optionType) ? (
          <SizedConfigFields optionType={optionType} config={config} fieldErrors={fieldErrors} onChange={onChange} />
        ) : null}
      </div>
    );
  }
  return <p className="text-sm text-text-secondary">This type has no extra configuration.</p>;
}

function SizedConfigFields({
  optionType,
  config,
  fieldErrors,
  onChange,
}: {
  optionType: string;
  config: Record<string, unknown>;
  fieldErrors: Map<string, string>;
  onChange: (config: Record<string, unknown>) => void;
}) {
  const sizing = asRecord(config.sizing);
  const binding = asRecord(config.variantBinding);
  const gang = optionType === 'GANGSHEET_UPLOAD_SIZED';
  const presets = Array.isArray(sizing.presets) ? sizing.presets.flatMap(presetRow) : [];
  const setSizing = (patch: Record<string, unknown>) => {
    onChange({ ...config, sizing: { ...sizing, ...patch } });
  };
  return (
    <div className="space-y-3 border-t border-border pt-3">
      <p className="text-sm font-medium text-text-primary">Print size</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Num
          label="Max width (in)"
          value={sizing.maxWidthIn}
          step="any"
          error={fieldErrors.get('sizing.maxWidthIn')}
          onChange={(maxWidthIn) => {
            if (maxWidthIn != null) setSizing({ maxWidthIn });
          }}
        />
        <Num
          label="Max height (in)"
          value={sizing.maxHeightIn}
          step="any"
          error={fieldErrors.get('sizing.maxHeightIn')}
          onChange={(maxHeightIn) => {
            if (maxHeightIn != null) setSizing({ maxHeightIn });
          }}
        />
        {gang ? null : (
          <Num
            label="Minimum size (in)"
            value={sizing.minSizeIn}
            step="any"
            error={fieldErrors.get('sizing.minSizeIn')}
            onChange={(minSizeIn) => {
              if (minSizeIn != null) setSizing({ minSizeIn });
            }}
          />
        )}
        {gang ? (
          <Num
            label="Sheet width (in)"
            value={binding.fixedWidthIn ?? 22}
            step="any"
            error={fieldErrors.get('variantBinding.fixedWidthIn')}
            onChange={(fixedWidthIn) => {
              if (fixedWidthIn != null) {
                onChange({ ...config, variantBinding: { ...binding, fixedWidthIn } });
              }
            }}
          />
        ) : (
          <Num
            label="+/− step (in)"
            value={sizing.stepIn}
            step="any"
            error={fieldErrors.get('sizing.stepIn')}
            onChange={(stepIn) => {
              if (stepIn != null) setSizing({ stepIn });
            }}
          />
        )}
        <Num
          label="Assumed DPI"
          value={sizing.assumedDpi}
          error={fieldErrors.get('sizing.assumedDpi')}
          onChange={(assumedDpi) => {
            if (assumedDpi != null) setSizing({ assumedDpi });
          }}
        />
        <p className="text-xs text-text-tertiary sm:col-span-2">
          {gang
            ? 'Print size uses the DPI saved in the file when it is between 36 and 1200, including 72. Assumed DPI is used only when the file has no DPI. Width and height are the trimmed print. Sheet width is the maximum.'
            : 'Print size uses the DPI saved in the file when it is between 36 and 1200, including 72. Assumed DPI is used only when the file has no DPI. The +/− step moves the size buttons. A typed size is accepted anywhere between the minimum and the maximum.'}
        </p>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={sizing.trim !== false}
          onChange={(event) => setSizing({ trim: event.target.checked })}
        />
        Trim transparent edges
      </label>
      <Num
        label="Trim alpha threshold (0–255)"
        value={sizing.trimAlphaThreshold}
        error={fieldErrors.get('sizing.trimAlphaThreshold')}
        onChange={(trimAlphaThreshold) => {
          if (trimAlphaThreshold != null) setSizing({ trimAlphaThreshold });
        }}
      />
      {gang ? null : (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={sizing.allowResize !== false}
            onChange={(event) => setSizing({ allowResize: event.target.checked })}
          />
          Customer can change the detected size
        </label>
      )}
      {gang ? null : (
        <div className="space-y-1">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={sizing.allowResize !== false && sizing.allowStretch !== false}
              disabled={sizing.allowResize === false}
              onChange={(event) => setSizing({ allowStretch: event.target.checked })}
            />
            Customer can unlock the proportions
          </label>
          <p className="pl-6 text-xs text-text-secondary">
            Unlocked, width and height are set separately within the limits and the artwork is
            stretched to that size. The detected proportions can always be restored.
          </p>
        </div>
      )}
      {gang ? null : (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={sizing.allowDetectedSize !== false}
            onChange={(event) => setSizing({ allowDetectedSize: event.target.checked })}
          />
          Offer the detected size
        </label>
      )}
      {gang ? null : (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-text-primary">Width presets</p>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                const used = new Set(presets.map((preset) => preset.id));
                setSizing({
                  presets: [
                    ...presets,
                    { id: nextPresetId('size', used), label: 'Size', widthIn: 11, active: true },
                  ],
                });
              }}
            >
              Add preset
            </Button>
          </div>
          <p className="text-xs text-text-tertiary">
            The preset width is the print width. Height follows the trimmed image.
          </p>
          {presets.map((preset, index) => (
            <div key={preset.id} className="grid gap-2 rounded-md border border-border p-3 sm:grid-cols-[1fr_8rem_auto_auto]">
              <label className="block text-sm">
                <span className="text-text-secondary">Name</span>
                <input
                  className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
                  value={preset.label}
                  onChange={(event) => {
                    const next = presets.slice();
                    const current = next[index];
                    if (!current) return;
                    next[index] = { ...current, label: event.target.value };
                    setSizing({ presets: next });
                  }}
                />
              </label>
              <Num
                label="Width (in)"
                value={preset.widthIn}
                step="any"
                error={fieldErrors.get(`sizing.presets.${index}.widthIn`)}
                onChange={(widthIn) => {
                  if (widthIn == null) return;
                  const next = presets.slice();
                  const current = next[index];
                  if (!current) return;
                  next[index] = { ...current, widthIn };
                  setSizing({ presets: next });
                }}
              />
              <label className="flex items-center gap-2 text-sm sm:mt-6">
                <input
                  type="checkbox"
                  checked={preset.active !== false}
                  onChange={(event) => {
                    const next = presets.slice();
                    const current = next[index];
                    if (!current) return;
                    next[index] = { ...current, active: event.target.checked };
                    setSizing({ presets: next });
                  }}
                />
                Active
              </label>
              <button
                type="button"
                className="text-sm text-danger-700 sm:mt-6"
                onClick={() => setSizing({ presets: presets.filter((item) => item.id !== preset.id) })}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SizePricingFields({
  optionType,
  config,
  blanks,
  onChange,
}: {
  optionType: string;
  config: Record<string, unknown>;
  blanks: PricingBlank[];
  onChange: (config: Record<string, unknown>) => void;
}) {
  const pricing = asRecord(config.sizePricing);
  const gang = optionType === 'GANGSHEET_UPLOAD_SIZED';
  const activeCount = blanks.filter((item) => item.isActive).length;
  const bandAllowed = !gang || activeCount >= 2;
  const storedBand = pricing.mode === 'VARIANT_BAND';
  const mode: string =
    pricing.mode === 'LONGEST_EDGE_RATE' ||
    pricing.mode === 'HEIGHT_RATE' ||
    (storedBand && bandAllowed)
      ? String(pricing.mode)
      : 'AREA_RATE';
  const selectedId = typeof pricing.pricingVariantId === 'string' ? pricing.pricingVariantId : '';
  const binding = asRecord(config.variantBinding);
  const setPricing = (patch: Record<string, unknown>) => {
    const nextMode = patch.mode;
    const nextBinding =
      gang && nextMode === 'VARIANT_BAND'
        ? { ...binding, defaultVariantRule: 'HEIGHT_CEIL', customerCanChangeVariant: false }
        : binding;
    onChange({
      ...config,
      sizePricing: { ...pricing, ...patch },
      ...(nextBinding === binding ? {} : { variantBinding: nextBinding }),
    });
  };
  return (
    <div className="space-y-3 border-t border-border pt-3">
      <label className="block text-sm">
        <span className="text-text-secondary">Size pricing</span>
        <select
          className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
          value={mode}
          onChange={(event) => setPricing({ mode: event.target.value })}
        >
          <option value="AREA_RATE">
            {gang ? 'Area × blank price (trimmed width × trimmed height × rate)' : 'Area × blank price (width × height × rate)'}
          </option>
          <option value="LONGEST_EDGE_RATE">Longest side × blank price</option>
          <option value="HEIGHT_RATE">Variant price × height</option>
          {bandAllowed ? (
            <option value="VARIANT_BAND">
              {gang ? 'Nearest taller variant price' : 'Nearest larger variant price'}
            </option>
          ) : null}
        </select>
        {gang && !bandAllowed ? (
          <span className={`mt-1 block text-xs ${storedBand ? 'text-danger-700' : 'text-text-tertiary'}`}>
            {storedBand
              ? 'This option is saved as nearest taller variant. Choose area, longest side, or height, then save.'
              : 'One variant uses area, longest side, or height. Nearest taller variant needs at least two variants, each with a height.'}
          </span>
        ) : null}
      </label>
      {mode === 'VARIANT_BAND' && bandAllowed ? (
        <label className="block text-sm">
          <span className="text-text-secondary">{gang ? 'Sheet height' : 'How a variant is chosen'}</span>
          <select
            className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
            value={
              gang
                ? 'HEIGHT_CEIL'
                : typeof binding.defaultVariantRule === 'string'
                  ? binding.defaultVariantRule
                  : 'LONGEST_EDGE_CEIL'
            }
            disabled={gang}
            onChange={(event) =>
              onChange({
                ...config,
                variantBinding: { ...binding, defaultVariantRule: event.target.value },
              })
            }
          >
            {gang ? null : <option value="LONGEST_EDGE_CEIL">Nearest longer edge</option>}
            <option value="HEIGHT_CEIL">Nearest taller variant</option>
            {gang ? null : <option value="WIDTH_CEIL">Nearest wider variant</option>}
            {gang ? null : <option value="AREA_CEIL">Nearest larger area</option>}
            {gang ? null : <option value="BOTH_EDGES_CEIL">Both edges must cover the artwork</option>}
          </select>
          <span className="mt-1 block text-xs text-text-tertiary">
            {gang
              ? 'Enter a height in inches on each variant. The sheet width stays fixed. The customer pays the shortest variant that covers the file height.'
              : 'Each blank needs a width and height in inches. The customer pays that variant’s price.'}
          </span>
        </label>
      ) : (
        <label className="block text-sm">
          <span className="text-text-secondary">Rate blank</span>
          <select
            className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
            value={selectedId}
            onChange={(event) => setPricing({ pricingVariantId: event.target.value || null })}
          >
            <option value="">Choose a blank</option>
            {blanks.filter((item) => item.isActive).map((item) => (
              <option key={item.id} value={item.id}>{item.sku}</option>
            ))}
          </select>
          <span className="mt-1 block text-xs text-text-tertiary">
            {gang
              ? 'The blank’s price is the rate. Area is trimmed width × trimmed height × rate. Longest side is the longer trimmed side × rate. Height is the detected trimmed height × rate. Sheet width is only the maximum.'
              : 'The blank’s price is the rate. Area uses it per square inch. Longest side uses it per inch of the longer side. Height uses it per inch of the detected height.'}
          </span>
        </label>
      )}
      {gang ? null : (
        <>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={binding.customerCanChangeVariant === true}
              onChange={(event) =>
                onChange({
                  ...config,
                  variantBinding: { ...binding, customerCanChangeVariant: event.target.checked },
                })
              }
            />
            Show the variant picker on the product page
          </label>
          <p className="text-xs text-text-tertiary">
            Off by default. The customer changes width and height instead of picking a variant.
            Turn this on only when they should choose the variant themselves.
          </p>
        </>
      )}
    </div>
  );
}

function presetRow(value: unknown): { id: string; label: string; widthIn: number; active: boolean }[] {
  if (!value || typeof value !== 'object') return [];
  const row = value as { id?: unknown; label?: unknown; widthIn?: unknown; active?: unknown };
  if (typeof row.id !== 'string' || typeof row.label !== 'string' || typeof row.widthIn !== 'number') return [];
  return [{ id: row.id, label: row.label, widthIn: row.widthIn, active: row.active !== false }];
}

function nextPresetId(label: string, used: Set<string>): string {
  const base = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
  let id = /^[a-z0-9]/.test(base) ? base : 'preset';
  let n = 2;
  while (used.has(id)) {
    id = `${base || 'preset'}-${n}`;
    n += 1;
  }
  return id;
}

function ChoicesEditor({
  optionType,
  values,
  fieldErrors,
  surchargeEnabled,
  onChange,
}: {
  optionType: string;
  values: ProductOptionChoice[];
  fieldErrors: Map<string, string>;
  surchargeEnabled: boolean;
  onChange: (values: ProductOptionChoice[]) => void;
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const rowIds = useRef<string[]>([]);
  const ids = alignRowIds(rowIds, values.length);
  const update = (index: number, patch: Partial<ProductOptionChoice>) => {
    onChange(values.map((choice, i) => (i === index ? { ...choice, ...patch } : choice)));
  };
  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    rowIds.current = arrayMove(ids, oldIndex, newIndex);
    onChange(arrayMove(values, oldIndex, newIndex).map((choice, index) => ({ ...choice, sortOrder: index })));
  };
  return (
    <div className="space-y-3">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {values.map((choice, index) => (
            <ChoiceRow
              key={ids[index]}
              rowId={ids[index]!}
              index={index}
              choice={choice}
              optionType={optionType}
              fieldErrors={fieldErrors}
              surchargeEnabled={surchargeEnabled}
              onUpdate={(patch) => update(index, patch)}
            onMakeDefault={() =>
              onChange(values.map((item, i) => ({ ...item, isDefault: i === index })))
            }
              onRemove={() => {
                rowIds.current = ids.filter((_, i) => i !== index);
                onChange(values.filter((_, i) => i !== index).map((item, i) => ({ ...item, sortOrder: i })));
              }}
            />
          ))}
        </SortableContext>
      </DndContext>
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          rowIds.current = [...ids, newRowId()];
          onChange([
            ...values,
            { key: '', label: '', sortOrder: values.length, isDefault: false },
          ]);
        }}
      >
        Add choice
      </Button>
    </div>
  );
}

function newRowId(): string {
  return crypto.randomUUID();
}

/** Row identity stays put while the label and key are typed. */
function alignRowIds(rowIds: { current: string[] }, count: number): string[] {
  const current = rowIds.current;
  if (current.length === count) return current;
  const next = current.slice(0, count);
  while (next.length < count) next.push(newRowId());
  rowIds.current = next;
  return next;
}

function ChoiceRow({
  rowId,
  index,
  choice,
  optionType,
  fieldErrors,
  surchargeEnabled,
  onUpdate,
  onMakeDefault,
  onRemove,
}: {
  rowId: string;
  index: number;
  choice: ProductOptionChoice;
  optionType: string;
  fieldErrors: Map<string, string>;
  surchargeEnabled: boolean;
  onUpdate: (patch: Partial<ProductOptionChoice>) => void;
  onMakeDefault: () => void;
  onRemove: () => void;
}) {
  const sortable = useSortable({ id: rowId });
  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
  };
  const labelError = fieldErrors.get(`${index}.label`) ?? fieldErrors.get(`values.${index}.label`);
  const keyError = fieldErrors.get(`${index}.key`) ?? fieldErrors.get(`values.${index}.key`);
  const singleDefault = isSingleSelectOptionType(optionType);
  const [swatchMode, setSwatchMode] = useState<'color' | 'image'>(choice.imageUrl ? 'image' : 'color');
  const [centsError, setCentsError] = useState<string | null>(null);
  return (
    <div ref={sortable.setNodeRef} style={style} className="space-y-2 rounded-md border border-border p-3">
      <div className="flex items-start gap-2">
        <button type="button" className="mt-2 cursor-grab text-text-tertiary" {...sortable.attributes} {...sortable.listeners}>
          ::
        </button>
        <div className="grid flex-1 gap-2 sm:grid-cols-2">
          <div>
            <input
              className="w-full rounded-md border border-border px-3 py-2 text-sm"
              placeholder="Label"
              value={choice.label}
              onChange={(event) => {
                const label = event.target.value;
                onUpdate({
                  label,
                  key: choice.key === slugOptionKey(choice.label) || choice.key === ''
                    ? slugOptionKey(label)
                    : choice.key,
                });
              }}
            />
            {labelError ? <p className="mt-1 text-xs text-danger-700">{labelError}</p> : null}
          </div>
          <div>
            <input
              className="w-full rounded-md border border-border px-3 py-2 font-mono text-sm"
              placeholder="key"
              value={choice.key}
              onChange={(event) => onUpdate({ key: event.target.value })}
            />
            {keyError ? <p className="mt-1 text-xs text-danger-700">{keyError}</p> : null}
          </div>
        </div>
      </div>
      {optionType === 'SWATCH' ? (
        <div className="space-y-2">
          <span className="text-sm text-text-secondary">Swatch</span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={swatchMode === 'color' ? 'primary' : 'outline'}
              onClick={() => {
                setSwatchMode('color');
                onUpdate({ imageUrl: null });
              }}
            >
              Color
            </Button>
            <Button
              size="sm"
              variant={swatchMode === 'image' ? 'primary' : 'outline'}
              onClick={() => {
                setSwatchMode('image');
                onUpdate({ colorHex: null });
              }}
            >
              Image
            </Button>
          </div>
          {swatchMode === 'color' ? (
            <label className="block text-sm">
              <span className="text-text-secondary">Color</span>
              <input
                type="color"
                className="mt-1 h-9 w-16 rounded-md border border-border bg-surface"
                value={choice.colorHex ?? '#111827'}
                onChange={(event) => onUpdate({ colorHex: event.target.value, imageUrl: null })}
              />
            </label>
          ) : (
            <ImageUploadInput
              label="Swatch image"
              value={choice.imageUrl ?? ''}
              directory="product-options"
              previewSize="sm"
              onChange={(imageUrl) => onUpdate({ imageUrl: imageUrl || null, colorHex: null })}
            />
          )}
        </div>
      ) : optionType === 'SELECT' ? null : (
        <ImageUploadInput
          label="Choice image"
          value={choice.imageUrl ?? ''}
          directory="product-options"
          previewSize="sm"
          onChange={(imageUrl) => onUpdate({ imageUrl: imageUrl || null })}
        />
      )}
      {surchargeEnabled ? (
        <label className="block text-sm">
          <span className="text-text-secondary">Choice price (cents)</span>
          <BufferedInput
            type="number"
            min={0}
            step={1}
            className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
            value={choice.priceDeltaCents == null ? '' : String(choice.priceDeltaCents)}
            onCommit={(raw) => {
              const parsed = wholeCents(raw);
              if (!parsed.ok) {
                setCentsError('Enter a whole number of cents. 50 is $0.50.');
                return;
              }
              setCentsError(null);
              onUpdate({ priceDeltaCents: parsed.cents ?? null });
            }}
          />
          <span className="mt-1 block text-xs text-text-tertiary">
            Whole cents. 50 is $0.50. Overrides the option charge for this choice.
          </span>
          {centsError ? <span className="mt-1 block text-xs text-danger-700">{centsError}</span> : null}
        </label>
      ) : null}
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm">
          <input
            type={singleDefault ? 'radio' : 'checkbox'}
            name={singleDefault ? 'tk-choice-default' : undefined}
            checked={choice.isDefault}
            onChange={(event) => {
              if (singleDefault) onMakeDefault();
              else onUpdate({ isDefault: event.target.checked });
            }}
          />
          Default
        </label>
        <Button size="sm" variant="outline" onClick={onRemove}>
          Remove
        </Button>
      </div>
    </div>
  );
}

const SIZE_AXES = ['x', 'y', 'z'] as const;

function DimensionFields({
  config,
  fieldErrors,
  onChange,
}: {
  config: Record<string, unknown>;
  fieldErrors: Map<string, string>;
  onChange: (config: Record<string, unknown>) => void;
}) {
  const converted = useRef(false);
  useEffect(() => {
    if (converted.current) return;
    converted.current = true;
    const parsed = customSizeConfigSchema.safeParse(config);
    if (parsed.success && (!('unit' in config) || 'minWidthIn' in config)) {
      onChange({ ...parsed.data });
    }
  }, [config, onChange]);

  const dimensions = config.dimensions === 3 ? 3 : 2;
  const axes = SIZE_AXES.slice(0, dimensions);
  const setGroup = (key: 'min' | 'max' | 'step' | 'defaults' | 'labels', axis: string, value: number | string | undefined) => {
    onChange({ ...config, [key]: { ...asRecord(config[key]), [axis]: value } });
  };
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="text-text-secondary">Unit</span>
          <select
            className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
            value={config.unit === 'cm' ? 'cm' : 'in'}
            onChange={(event) => onChange({ ...config, unit: event.target.value })}
          >
            <option value="in">Inches</option>
            <option value="cm">Centimeters</option>
          </select>
        </label>
        <label className="block text-sm">
          <span className="text-text-secondary">Dimensions</span>
          <select
            className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
            value={String(dimensions)}
            onChange={(event) => onChange({ ...config, dimensions: event.target.value === '3' ? 3 : 2 })}
          >
            <option value="2">Width and height</option>
            <option value="3">Width, height, and depth</option>
          </select>
        </label>
      </div>
      {axes.map((axis) => (
        <div key={axis} className="grid gap-3 sm:grid-cols-5">
          <label className="block text-sm">
            <span className="text-text-secondary">Label</span>
            <input
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
              value={typeof asRecord(config.labels)[axis] === 'string' ? String(asRecord(config.labels)[axis]) : ''}
              onChange={(event) => setGroup('labels', axis, event.target.value)}
            />
          </label>
          <Num label="Min" value={asRecord(config.min)[axis]} error={fieldErrors.get(`config.min.${axis}`)} onChange={(value) => setGroup('min', axis, value)} />
          <Num label="Max" value={asRecord(config.max)[axis]} error={fieldErrors.get(`config.max.${axis}`)} onChange={(value) => setGroup('max', axis, value)} />
          <Num label="Step" value={asRecord(config.step)[axis]} step="any" error={fieldErrors.get(`config.step.${axis}`)} onChange={(value) => setGroup('step', axis, value)} />
          <Num label="Default" value={asRecord(config.defaults)[axis]} step="any" onChange={(value) => setGroup('defaults', axis, value)} />
        </div>
      ))}
    </div>
  );
}

function DefaultValueField({
  optionType,
  value,
  onChange,
}: {
  optionType: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}) {
  if (['RADIO', 'SELECT', 'SWATCH', 'BUTTON_GROUP', 'CHECKBOX_GROUP'].includes(optionType)) {
    return <p className="text-sm text-text-secondary">Set the default on the Choices tab.</p>;
  }
  if (optionType === 'CUSTOM_SIZE') {
    return <p className="text-sm text-text-secondary">Set the starting size on the Configuration tab.</p>;
  }
  if (optionType === 'INFO' || UPLOAD_TYPES.has(optionType)) return null;
  if (optionType === 'CHECKBOX' || optionType === 'BOOLEAN') {
    return (
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={value === 'true'}
          onChange={(event) => onChange(event.target.checked ? 'true' : undefined)}
        />
        Checked by default
      </label>
    );
  }
  return (
    <label className="block text-sm">
      <span className="text-text-secondary">Default value</span>
      <input
        type={optionType === 'NUMBER' ? 'number' : optionType === 'DATE' ? 'date' : 'text'}
        className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value || undefined)}
      />
    </label>
  );
}

function Num({
  label,
  value,
  onChange,
  error,
  step,
}: {
  label: string;
  value: unknown;
  onChange: (value: number | undefined) => void;
  error?: string;
  step?: number | 'any';
}) {
  return (
    <label className="block text-sm">
      <span className="text-text-secondary">{label}</span>
      <BufferedInput
        type="number"
        step={step}
        className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
        value={typeof value === 'number' ? String(value) : ''}
        onCommit={(raw) => onChange(optionalNumber(raw))}
      />
      {error ? <p className="mt-1 text-xs text-danger-700">{error}</p> : null}
    </label>
  );
}

function BufferedInput({
  value,
  onCommit,
  className,
  type = 'text',
  placeholder,
  min,
  step,
}: {
  value: string;
  onCommit: (raw: string) => void;
  className: string;
  type?: string;
  placeholder?: string;
  min?: number;
  step?: number | 'any';
}) {
  const [text, setText] = useState<string | null>(null);
  return (
    <input
      type={type}
      min={min}
      step={step}
      placeholder={placeholder}
      className={className}
      value={text ?? value}
      onChange={(event) => {
        setText(event.target.value);
        onCommit(event.target.value);
      }}
      onBlur={() => setText(null)}
    />
  );
}

function wholeCents(raw: string): { ok: true; cents: number | undefined } | { ok: false } {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: true, cents: undefined };
  if (!/^\d+$/.test(trimmed)) return { ok: false };
  return { ok: true, cents: Number(trimmed) };
}

function optionalNumber(raw: string): number | undefined {
  if (raw.trim() === '') return undefined;
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
}

function issueMap(issues: { path: PropertyKey[]; message: string }[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const issue of issues) {
    const path = issue.path.map(String).join('.');
    if (!map.has(path)) map.set(path, issue.message);
  }
  return map;
}

function summarize(issues: { path: PropertyKey[]; message: string }[]): string {
  return issues
    .slice(0, 5)
    .map((issue) => {
      const path = issue.path.map(String).join('.');
      return path ? `${path}: ${issue.message}` : issue.message;
    })
    .join('; ');
}

function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      {label}
    </label>
  );
}

export interface PricingBlank {
  id: string;
  sku: string;
  cost: string;
  /** Blank cost plus axis surcharge, the portal formula rate. */
  portalUnitCost?: string;
  msrp: string | null;
  isActive: boolean;
}

function DimensionPricePicker({
  config,
  blanks,
  error,
  onChange,
}: {
  config: unknown;
  blanks: PricingBlank[];
  error?: string;
  onChange: (pricingVariantId: string | null) => void;
}) {
  const parsed = customSizeConfigSchema.safeParse(config ?? {});
  const unit = parsed.success ? parsed.data.unit : 'in';
  const selectedId = parsed.success && parsed.data.pricingVariantId ? parsed.data.pricingVariantId : '';
  const blank = blanks.find((item) => item.id === selectedId);
  return (
    <div className="space-y-2 border-t border-border pt-3">
      <label className="block text-sm">
        <span className="text-text-secondary">Size pricing</span>
        <select
          className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
          value={selectedId}
          onChange={(event) => onChange(event.target.value || null)}
        >
          <option value="">Not priced (size is saved with the order only)</option>
          {blanks.filter((item) => item.isActive).map((item) => (
            <option key={item.id} value={item.id}>{item.sku}</option>
          ))}
        </select>
      </label>
      {blank ? (
        <p className="text-xs text-text-secondary">
          Rate: the published price of this blank per square {unit}. Price = width × height × rate.
          Storefront uses the Shopify price of this blank; portal uses its catalog cost
          {blank.msrp ? ` (catalog cost ${blank.cost}, MSRP ${blank.msrp})` : ` (catalog cost ${blank.cost})`}.
          Enter the price per square {unit} on the blank itself; it is not converted between inches and centimeters.
        </p>
      ) : (
        <p className="text-xs text-text-secondary">
          Choose a blank to price this size as width × height × that rate. The price is per square {unit} of the
          blank and is not converted between inches and centimeters.
        </p>
      )}
      {error ? <p className="text-xs text-danger-700">{error}</p> : null}
    </div>
  );
}

function asRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value as Record<string, unknown>;
}
